import * as Sentry from '@sentry/react';

import { type SelectedUserData } from '~/data/user/useQueryUserData';

import { isLocalhost } from '~/utils/environment';
import i18n from '~/utils/i18n';

import { getBugReportContext } from './utils';
import { type BugReportFormValues } from './validations';

type P = {
  readonly currentUser: SelectedUserData | undefined;
  readonly harFile?: File;
  readonly screenshot?: File;
  readonly values: BugReportFormValues;
};

async function toAttachment(
  file: File,
): Promise<{ data: Uint8Array; filename: string }> {
  return {
    data: new Uint8Array(await file.arrayBuffer()),
    filename: file.name,
  };
}

/**
 * Submits a bug report to Sentry as user feedback, enriched with a snapshot of
 * the current user's feature flags, permissions, route, viewport and user
 * agent.
 *
 * For internal Vestigas-support users we additionally set `internalUser=true`
 * as an event tag. A Sentry issue alert rule (configured in the Sentry UI)
 * filters on `issue.category = Feedback` + `tag internalUser = true` and
 * creates a Linear issue via the Sentry<->Linear integration. Changing the
 * tag name requires updating that alert rule.
 */
export const submitBugReport = async ({
  currentUser,
  harFile,
  screenshot,
  values,
}: P) => {
  const {
    actualBehavior,
    expectedBehavior,
    reporterName,
    stepsToReproduce,
    title,
    videoLink,
  } = values;

  const context = getBugReportContext(currentUser);

  // Build the Sentry message. The first line becomes the Linear issue title
  // via the Sentry ↔ Linear integration.
  const message = [
    title,
    '',
    `${i18n.t(($) => $.dialog.stepsToReproduce.label, { ns: 'bugReport' })}:`,
    stepsToReproduce,
    '',
    `${i18n.t(($) => $.dialog.expectedBehavior.label, { ns: 'bugReport' })}:`,
    expectedBehavior,
    '',
    `${i18n.t(($) => $.dialog.actualBehavior.label, { ns: 'bugReport' })}:`,
    actualBehavior,
    ...(videoLink ? ['', `🎥 Video: ${videoLink}`] : []),
  ].join('\n');

  // Don't send bug reports to Sentry from localhost — Sentry is disabled there
  // anyway (see `ThirdPartyService.initSentry`). Log the payload so developers
  // can verify the dialog works without producing Sentry noise.
  if (isLocalhost) {
    console.info('[bugReport] Skipped submit on localhost', {
      context,
      harFile,
      message,
      reporterName,
      screenshot,
    });

    return;
  }

  const attachments = await Promise.all(
    [screenshot, harFile]
      .filter((file): file is File => Boolean(file))
      .map(toAttachment),
  );

  Sentry.withScope((scope) => {
    scope.setContext('bugReport', {
      apiEnvironment: context.apiEnvironment,
      appEnvironment: context.appEnvironment,
      company: context.company,
      companyAccount: context.companyAccount,
      featureFlags: context.featureFlags,
      permissions: context.permissions,
      route: context.route,
      userAgent: context.userAgent,
      viewport: context.viewport,
    });

    scope.setTag('bugReport', 'true');
    // Searchable in Sentry; lets you filter feedback by API backend.
    // The app environment (e.g. "production") is already set globally via
    // Sentry.init({ environment }) — the API environment can differ when the
    // dev env switcher is used (e.g. app host = dev, API = DEV3).
    scope.setTag('apiEnvironment', context.apiEnvironment);

    if (currentUser?.isVestigasSupport) {
      scope.setTag('internalUser', 'true');
    }

    Sentry.captureFeedback(
      {
        email: currentUser?.email ?? undefined,
        message,
        name: reporterName,
      },
      attachments.length > 0 ? { attachments } : undefined,
    );
  });
};
